
import Vue from 'vue';
import FilterChipTemplate from './FilterChipTemplate.vue';
import DeviceService from '@/services/DeviceService';
import { autoComplete } from '@/utils/Util';
import ConnectorService from '@/services/ConnectorService';
import PropertyService from '@/services/PropertyService';

export default Vue.extend({
  name: 'DeviceFilter',

  components: {
    FilterChipTemplate,
  },
  props: {
    value: {},
    label: {},
    attributes: {},
    mandatory: {},
    always: {},
    multi: {},
    small: {},
    connectorKey: { type: String, default: 'connectorId' },
    propertyKey: { type: String, default: 'propertyId' },
  },
  data: (_this: any): any => ({
    rules: [
      (v: any) => !!v || _this.requiredError,
    ],
    localDevice: _this.value[_this.attributes['value']],
    localFilters: _this.value as any,
    menu: false,
    loading: false,
    devices: [],
    localConnectorId: undefined,
    localPropertyId: undefined,
    connectorAssociatedDevices: [],
    propertyAssociatedDevices: [],
  }),
  created() {
    this.getDevices();
    this.loadAssociatedFromExistingFilters();
  },
  methods: {
    loadAssociatedFromExistingFilters() {
      if (this.localFilters[this.connectorKey]) {
        this.localConnectorId = this.localFilters[this.connectorKey];
        this.getConnectorAssociatedDevices();
      }
      if (this.localFilters[this.propertyKey]) {
        this.localPropertyId = this.localFilters[this.propertyKey];
        this.getPropertyAssociatedDevices();
      }
    },

    onSave() {
      this.localFilters[this.attributes['value']] = this.localDevice;
      this.$emit('input', { ...this.localFilters });
      this.menu = false;
    },
    onReset() {
      this.$delete(this.localFilters, this.attributes['value']);
      this.$emit('input', { ...this.localFilters });
      this.menu = false;
    },
    onChipRemove() {
      this.$delete(this.localFilters, this.attributes['value']);
      this.$emit('input', { ...this.localFilters });
      this.$emit('delete');
    },
    autoComplete,
    getDevices() {
      this.devices = [];
      this.loadingDevices = true;

      DeviceService.getAllCollectionPages(this.currentTenant)
        .then((devices: any[]) => {
          this.loadingDevices = false;

          this.devices = devices;
        })
        .catch((err: any) => {
          console.log(err, err.response);
          this.loadingDevices = false;
        });
    },
    getConnectorAssociatedDevices() {
      this.connectorAssociatedDevices = [];
      this.loading = true;

      ConnectorService.getAssociatedDevices(
        this.currentTenant,
        this.localFilters[this.connectorKey]
      )
        .then((devices: any[]) => {
          this.loading = false;
          this.connectorAssociatedDevices = devices;
        })
        .catch((err: any) => {
          console.log(err, err.response);
          this.loading = false;
        });
    },
    getPropertyAssociatedDevices() {
      this.propertyAssociatedDevices = [];
      this.loading = true;

      PropertyService.getAssociatedDevices(
        this.currentTenant,
        this.localFilters[this.propertyKey]
      )
        .then((devices: any[]) => {
          this.loading = false;
          this.propertyAssociatedDevices = devices;
        })
        .catch((err: any) => {
          console.log(err, err.response);
          this.loading = false;
        });
    },
  },
  watch: {
    value() {
      this.localFilters = { ...this.value };

      // handle filtering of devices when property or connector filters are applied
      if (
        this.localFilters[this.connectorKey] &&
        this.localFilters[this.connectorKey] !== this.localConnectorId
      ) {
        this.localConnectorId = this.localFilters[this.connectorKey];
        this.$delete(this.localFilters, this.attributes['value']);
        this.$emit('input', { ...this.localFilters });
        this.getConnectorAssociatedDevices();
      } else if (!this.localFilters[this.connectorKey]) {
        this.connectorAssociatedDevices = [];
        this.localConnectorId = undefined;
      }
      if (
        this.localFilters[this.propertyKey] &&
        this.localFilters[this.propertyKey] !== this.localPropertyId
      ) {
        this.localPropertyId = this.localFilters[this.propertyKey];
        this.$delete(this.localFilters, this.attributes['value']);
        this.$emit('input', { ...this.localFilters });
        this.getPropertyAssociatedDevices();
      } else if (!this.localFilters[this.propertyKey]) {
        this.propertyAssociatedDevices = [];
        this.localPropertyId = undefined;
      }

      this.localDevice = this.localFilters[this.attributes['value']];
    },

    menu(menuOpened) {
      if (menuOpened) {
        const propertyId = this.localFilters[this.propertyKey] || null;
        const connectorId = this.localFilters[this.connectorKey] || null;

        this.localDevice = this.localFilters[this.attributes['value']] || null;

        if (propertyId && this.propertyAssociatedDevices.length === 0) {
          this.getPropertyAssociatedDevices();
        }

        if (connectorId && this.connectorAssociatedDevices.length === 0) {
          this.getConnectorAssociatedDevices();
        }
      }
    },
    currentTenant() {
      this.devices = [];
      this.propertyAssociatedDevices = [];
      this.connectorAssociatedDevices = [];
      this.localFilters = { ...this.value }; // TODO
      this.getDevices();
    },
  },
  computed: {
    filteredDevices(): any[] {
      if (!this.localFilters[this.connectorKey] && !this.localFilters[this.propertyKey]) {
        return this.devices;
      } else if (this.localFilters[this.connectorKey] && this.localFilters[this.propertyKey]) {
        return this.devices.filter(
          (value: any) =>
            this.connectorAssociatedDevices.findIndex(
              (cd: any) => cd.uuid === value.uuid
            ) >= 0 &&
            this.propertyAssociatedDevices.findIndex(
              (pd: any) => pd.uuid === value.uuid
            ) >= 0
        );
      } else if (this.localFilters[this.connectorKey] || this.localFilters[this.propertyKey]) {
        return this.devices.filter(
          (value: any) =>
            this.connectorAssociatedDevices.findIndex(
              (cd: any) => cd.uuid === value.uuid
            ) >= 0 ||
            this.propertyAssociatedDevices.findIndex(
              (pd: any) => pd.uuid === value.uuid
            ) >= 0
        );
      }

      return this.devices;
    },
    isValid(): any {
      return this.mandatory
        ? this.rules.reduce(
            (acc: boolean, rule: any) =>
              acc ? rule(this.localDevice) === true : acc,
            true
          )
        : true;
    },
    requiredError(): any {
      return this.$t('required');
    },
    filterRepr(): any {
      const raw = this.localFilters[this.attributes['value']];
      if (!raw) return '';
      if (this.multi) {
        return (raw as any[]).map((uuid: any) => {
          const found = this.devices.find((d: any) => d.uuid === uuid);
          return found ? found.aksId : uuid;
        }).join(', ');
      }
      const found = this.devices.find((d: any) => d.uuid === raw);
      return found ? found.aksId : raw;
    },
    currentTenant(): any {
      return this.$root.$store.state.session.selectedTenant.uuid;
    },
  },
});
